import Box from "@mui/material/Box"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiCheckbox from "components/MuiCheckbox"
import MuiTextField from "components/MuiTextField"
import { useGetPayoutDealTermsQuery } from "pages/Common/commonService"
import { useCallback, useState } from "react"
import { ChargerRow } from "./useSaas"

interface ChargerDetailsTableProps {
    chargers: ChargerRow[]
    onChargerChange: (index: number, field: keyof ChargerRow, value: string) => void
    onBulkChargerUpdate: (indices: number[], fields: Partial<ChargerRow>) => void
    chargerErrors?: Record<number, Set<string>>
}

type BulkEditFields = {
    ops_power_rating: string
    cpo_cost: string
    host_profit: string
    host_rent: string
    host_profit_amt: string
    host_profit_type: string
    deal_terms: string
}

const clampPercent = (value: string) => {
    if (value === "") return ""
    const num = Number(value)
    if (Number.isNaN(num)) return value
    if (num > 100) return "100"
    if (num < 0) return "0"
    return value
}

const EMPTY_BULK_FIELDS: BulkEditFields = {
    ops_power_rating: "",
    cpo_cost: "",
    host_profit: "",
    host_rent: "",
    host_profit_amt: "",
    host_profit_type: "",
    deal_terms: "",
}

const tableHeaderCellStyles = {
    color: "#70758D",
    fontSize: "11px",
    padding: "4px 8px",
    border: "1px solid #E5E7EB",
    fontWeight: 600,
    backgroundColor: "#FAFAFA",
}

const tableCellStyles = {
    color: "#101941",
    fontSize: "12px",
    padding: "0",
    border: "1px solid #E5E7EB",
    height: "36px",
}

const editableCellStyles = {
    backgroundColor: "#FFFFFF",
}

const readOnlyCellStyles = {
    backgroundColor: "#F9FAFB",
}

const ChargerDetailsTable = ({
    chargers,
    onChargerChange,
    onBulkChargerUpdate,
    chargerErrors = {},
}: ChargerDetailsTableProps) => {
    const hasFieldError = (index: number, field: string) => chargerErrors[index]?.has(field) ?? false
    const { data: payoutDealTerms, isFetching: isDealTermsFetching } = useGetPayoutDealTermsQuery()
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
    const [bulkFields, setBulkFields] = useState<BulkEditFields>({ ...EMPTY_BULK_FIELDS })

    const payoutDealTermOptions =
        payoutDealTerms?.map(i => ({
            id: i.id,
            title: i.name,
        })) || []

    const isAllSelected = chargers.length > 0 && selectedRows.size === chargers.length
    const isIndeterminate = selectedRows.size > 0 && selectedRows.size < chargers.length
    const hasSelection = selectedRows.size > 0

    const handleSelectAll = useCallback(() => {
        setSelectedRows(prev => {
            if (prev.size === chargers.length) return new Set()
            return new Set(chargers.map((_, i) => i))
        })
    }, [chargers])

    const handleSelectRow = useCallback((index: number) => {
        setSelectedRows(prev => {
            const next = new Set(prev)
            if (next.has(index)) {
                next.delete(index)
            } else {
                next.add(index)
            }
            return next
        })
    }, [])

    const handleBulkFieldChange = useCallback((field: keyof BulkEditFields, value: string) => {
        setBulkFields(prev => ({ ...prev, [field]: value }))
    }, [])

    const handleApplyBulk = useCallback(() => {
        const filledFields = Object.fromEntries(
            Object.entries(bulkFields).filter(([_, value]) => value !== "")
        ) as Partial<ChargerRow>
        if (Object.keys(filledFields).length === 0) return
        onBulkChargerUpdate(Array.from(selectedRows), filledFields)
        setBulkFields({ ...EMPTY_BULK_FIELDS })
        setSelectedRows(new Set())
    }, [selectedRows, bulkFields, onBulkChargerUpdate])

    const errorInputSx = {
        "& .MuiInputBase-root": { borderRadius: 0, height: "100%" },
        "& .MuiInputBase-input": { padding: "4px 8px" },
    }
    const errorHighlightSx = (index: number, field: string) => ({
        height: "100%",
        ...errorInputSx,
        ...(hasFieldError(index, field) && {
            "& .MuiInputBase-root": {
                ...errorInputSx["& .MuiInputBase-root"],
                borderColor: "#d32f2f",
            },
        }),
    })
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {hasSelection && (
                <Box
                    display={"flex"}
                    flexDirection={"column"}
                    p="8px 12px"
                    borderRadius="8px"
                    border="1px solid #E5E7EB">
                    <Typography variant="h6" fontSize="14px" fontWeight={600} pb={0.5}>
                        Bulk Update:
                    </Typography>
                    <Box display="flex" alignItems="flex-end" gap={1.5}>
                        <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
                            <Typography variant="label" fontSize="11px">
                                Ops Power Rating
                            </Typography>
                            <MuiTextField
                                value={bulkFields.ops_power_rating}
                                placeholder="Enter"
                                type="number"
                                size="small"
                                onChange={e => handleBulkFieldChange("ops_power_rating", e.target.value)}
                                sx={{ "& .MuiInputBase-input": { padding: "6px 8px" } }}
                            />
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
                            <Typography variant="label" fontSize="11px">
                                Charging Price
                            </Typography>
                            <MuiTextField
                                value={bulkFields.cpo_cost}
                                placeholder="₹"
                                type="number"
                                size="small"
                                onChange={e => handleBulkFieldChange("cpo_cost", e.target.value)}
                                sx={{ "& .MuiInputBase-input": { padding: "6px 8px" } }}
                            />
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
                            <Typography variant="label" fontSize="11px">
                                Deal Applicable
                            </Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                options={payoutDealTermOptions}
                                loading={isDealTermsFetching}
                                value={
                                    payoutDealTermOptions.find(
                                        option => String(option.id) === String(bulkFields.deal_terms)
                                    ) || null
                                }
                                placeholder="Select"
                                size="small"
                                onChange={(value: any) =>
                                    handleBulkFieldChange("deal_terms", value ? String(value.id) : "")
                                }
                                sx={{ "& .MuiInputBase-input": { padding: "6px 8px" } }}
                            />
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
                            <Typography variant="label" fontSize="11px">
                                Host Profit Share
                            </Typography>
                            <MuiTextField
                                value={bulkFields.host_profit}
                                placeholder="%"
                                type="number"
                                size="small"
                                onChange={e => handleBulkFieldChange("host_profit", clampPercent(e.target.value))}
                                sx={{ "& .MuiInputBase-input": { padding: "6px 8px" } }}
                            />
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
                            <Typography variant="label" fontSize="11px">
                                Host Rental
                            </Typography>
                            <MuiTextField
                                value={bulkFields.host_rent}
                                placeholder="₹"
                                type="number"
                                size="small"
                                onChange={e => handleBulkFieldChange("host_rent", e.target.value)}
                                sx={{ "& .MuiInputBase-input": { padding: "6px 8px" } }}
                            />
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.5} flex={1.5}>
                            <Typography variant="label" fontSize="11px">
                                Host Revenue Share
                            </Typography>
                            <Box display="flex" gap={0.5}>
                                <MuiTextField
                                    value={bulkFields.host_profit_amt}
                                    placeholder={bulkFields.host_profit_type === "0" ? "%" : "₹"}
                                    type="number"
                                    size="small"
                                    onChange={e =>
                                        handleBulkFieldChange(
                                            "host_profit_amt",
                                            bulkFields.host_profit_type === "0"
                                                ? clampPercent(e.target.value)
                                                : e.target.value
                                        )
                                    }
                                    sx={{ flex: 1, "& .MuiInputBase-input": { padding: "6px 8px" } }}
                                />
                                <Select
                                    value={bulkFields.host_profit_type}
                                    displayEmpty
                                    onChange={e => handleBulkFieldChange("host_profit_type", e.target.value as string)}
                                    sx={{
                                        flex: 1,
                                        fontSize: "12px",
                                        "& .MuiSelect-select": { padding: "6px 8px" },
                                    }}>
                                    <MenuItem value="" disabled>
                                        Select
                                    </MenuItem>
                                    <MenuItem value="0">% (Percentage)</MenuItem>
                                    <MenuItem value="1">Per Unit</MenuItem>
                                </Select>
                            </Box>
                        </Box>
                        <Typography
                            onClick={handleApplyBulk}
                            sx={{
                                color: "statiqOrange.main",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                                pb: "6px",
                            }}>
                            Done
                        </Typography>
                    </Box>
                </Box>
            )}
            <TableContainer
                sx={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    overflowX: "auto",
                }}>
                <Table stickyHeader size="small" sx={{ minWidth: "1500px" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 40, padding: "0 4px" }}>
                                <MuiCheckbox
                                    size="small"
                                    checked={isAllSelected}
                                    indeterminate={isIndeterminate}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 50 }}>S.No</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 120 }}>SKU Code</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 150 }}>Charger Name</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 130 }}>Ops Power Rating</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 130 }}>Connector Count</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 120 }}>Charging Price</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 140 }}>Deal Applicable</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 140 }}>Host Profit Share</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles, width: 120 }}>Host Rental</TableCell>
                            <TableCell sx={{ ...tableHeaderCellStyles }} colSpan={2}>
                                Host Revenue Share
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {chargers.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={13}
                                    sx={{ ...tableCellStyles, textAlign: "center", color: "#70758D", padding: "12px" }}>
                                    No chargers available
                                </TableCell>
                            </TableRow>
                        ) : (
                            chargers.map((charger, index) => (
                                <TableRow
                                    key={charger.charger_id}
                                    sx={selectedRows.has(index) ? { backgroundColor: "#F0F4FF" } : undefined}>
                                    <TableCell sx={{ ...tableCellStyles, ...readOnlyCellStyles, padding: "0 4px" }}>
                                        <MuiCheckbox
                                            size="small"
                                            checked={selectedRows.has(index)}
                                            onChange={() => handleSelectRow(index)}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...readOnlyCellStyles, padding: "4px 8px" }}>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...readOnlyCellStyles }}>
                                        <Box sx={{ padding: "4px 8px" }}>
                                            <Typography fontSize="12px">{charger.sku || "-"}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <MuiTextField
                                            value={charger.name}
                                            placeholder="Enter name"
                                            size="small"
                                            onChange={e => onChargerChange(index, "name", e.target.value)}
                                            sx={errorInputSx}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <MuiTextField
                                            value={charger.ops_power_rating}
                                            placeholder="Enter"
                                            type="number"
                                            size="small"
                                            error={hasFieldError(index, "ops_power_rating")}
                                            onChange={e => onChargerChange(index, "ops_power_rating", e.target.value)}
                                            sx={errorHighlightSx(index, "ops_power_rating")}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...readOnlyCellStyles }}>
                                        <Box sx={{ padding: "4px 8px" }}>
                                            <Typography fontSize="12px">{charger.connector_count || "-"}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <MuiTextField
                                            value={charger.cpo_cost}
                                            placeholder="₹"
                                            type="number"
                                            size="small"
                                            error={hasFieldError(index, "cpo_cost")}
                                            onChange={e => onChargerChange(index, "cpo_cost", e.target.value)}
                                            sx={errorHighlightSx(index, "cpo_cost")}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <MuiAutoComplete
                                            disablePortal={false}
                                            options={payoutDealTermOptions}
                                            loading={isDealTermsFetching}
                                            value={
                                                payoutDealTermOptions.find(
                                                    option => String(option.id) === String(charger.deal_terms)
                                                ) || null
                                            }
                                            placeholder="Select"
                                            size="small"
                                            placement="top"
                                            error={hasFieldError(index, "deal_terms")}
                                            onChange={(value: any) =>
                                                onChargerChange(index, "deal_terms", value ? String(value.id) : "")
                                            }
                                            sx={errorHighlightSx(index, "deal_terms")}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <MuiTextField
                                            value={charger.host_profit}
                                            placeholder="%"
                                            type="number"
                                            size="small"
                                            error={hasFieldError(index, "host_profit")}
                                            onChange={e =>
                                                onChargerChange(index, "host_profit", clampPercent(e.target.value))
                                            }
                                            sx={errorHighlightSx(index, "host_profit")}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <MuiTextField
                                            value={charger.host_rent}
                                            placeholder="₹"
                                            type="number"
                                            size="small"
                                            error={hasFieldError(index, "host_rent")}
                                            onChange={e => onChargerChange(index, "host_rent", e.target.value)}
                                            sx={errorHighlightSx(index, "host_rent")}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <MuiTextField
                                            value={charger.host_profit_amt}
                                            placeholder={charger.host_profit_type === "0" ? "%" : "₹"}
                                            type="number"
                                            size="small"
                                            fullWidth
                                            error={hasFieldError(index, "host_profit_amt")}
                                            onChange={e =>
                                                onChargerChange(
                                                    index,
                                                    "host_profit_amt",
                                                    charger.host_profit_type === "0"
                                                        ? clampPercent(e.target.value)
                                                        : e.target.value
                                                )
                                            }
                                            sx={errorHighlightSx(index, "host_profit_amt")}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...tableCellStyles, ...editableCellStyles }}>
                                        <Select
                                            value={charger.host_profit_type}
                                            displayEmpty
                                            error={hasFieldError(index, "host_profit_type")}
                                            onChange={e =>
                                                onChargerChange(index, "host_profit_type", e.target.value as string)
                                            }
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: 0,
                                                fontSize: "12px",
                                                "& .MuiSelect-select": { padding: "4px 8px" },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    border: hasFieldError(index, "host_profit_type")
                                                        ? "1px solid #d32f2f"
                                                        : "none",
                                                },
                                            }}>
                                            <MenuItem value="" disabled>
                                                Select
                                            </MenuItem>
                                            <MenuItem value="0">% (Percentage)</MenuItem>
                                            <MenuItem value="1">Per Unit</MenuItem>
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default ChargerDetailsTable
